import {
  TextField, Table, TableHead, TableRow, TableCell, TableBody,
  Checkbox, IconButton, Typography,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { useCaseFieldData, useCaseFieldDispatch } from '../../../hooks/useCaseField';
import type { FieldMode } from '../../../hooks/useCaseField';
import type { ProcedureDetailView } from '../../../../../services/MedNecService/types/medNecTypes';

const emptyProcedure: ProcedureDetailView = {
  cptCode: '',
  description: null,
  level: null,
  approach: null,
  side: null,
  isPrimary: false,
};

interface ProcedureTableProps {
  mode: FieldMode;
}

const ProcedureTable: React.FC<ProcedureTableProps> = ({ mode }) => {
  const data = useCaseFieldData(mode);
  const { dispatchUpdate } = useCaseFieldDispatch(mode);
  const procedures = data.procedures ?? [];

  const updateProcedure = (index: number, fields: Partial<ProcedureDetailView>) => {
    const updated = procedures.map((p, i) => i === index ? { ...p, ...fields } : p);
    dispatchUpdate({ procedures: updated });
  };

  const addProcedure = () => {
    dispatchUpdate({ procedures: [...procedures, { ...emptyProcedure }] });
  };

  const removeProcedure = (index: number) => {
    dispatchUpdate({ procedures: procedures.filter((_, i) => i !== index) });
  };

  if (procedures.length === 0) {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No procedures added.
        </Typography>
        <IconButton size="small" onClick={addProcedure} sx={{ mt: 1 }}>
          <AddIcon fontSize="small" />
        </IconButton>
      </>
    );
  }

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>CPT</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Level</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Approach</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Side</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">Primary</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {procedures.map((proc, i) => (
            <TableRow key={i}>
              <TableCell sx={{ p: 0.5 }}>
                <TextField
                  size="small" variant="standard"
                  value={proc.cptCode}
                  onChange={e => updateProcedure(i, { cptCode: e.target.value })}
                  sx={{ width: 90 }}
                />
              </TableCell>
              <TableCell sx={{ p: 0.5 }}>
                <TextField
                  size="small" variant="standard" fullWidth
                  value={proc.description ?? ''}
                  onChange={e => updateProcedure(i, { description: e.target.value })}
                />
              </TableCell>
              <TableCell sx={{ p: 0.5 }}>
                <TextField
                  size="small" variant="standard"
                  value={proc.level ?? ''}
                  onChange={e => updateProcedure(i, { level: e.target.value })}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell sx={{ p: 0.5 }}>
                <TextField
                  size="small" variant="standard"
                  value={proc.approach ?? ''}
                  onChange={e => updateProcedure(i, { approach: e.target.value })}
                  sx={{ width: 100 }}
                />
              </TableCell>
              <TableCell sx={{ p: 0.5 }}>
                <TextField
                  size="small" variant="standard"
                  value={proc.side ?? ''}
                  onChange={e => updateProcedure(i, { side: e.target.value })}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell align="center" sx={{ p: 0.5 }}>
                <Checkbox
                  checked={proc.isPrimary}
                  onChange={e => updateProcedure(i, { isPrimary: e.target.checked })}
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ p: 0.5 }}>
                <IconButton size="small" onClick={() => removeProcedure(i)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <IconButton size="small" onClick={addProcedure} sx={{ mt: 1 }}>
        <AddIcon fontSize="small" />
      </IconButton>
    </>
  );
};

export default ProcedureTable;
